import React, { useState, useEffect, useRef, useCallback } from 'react';
import './DateTimePicker.css'; // Reuse the same styles

interface ScrollableSelectProps {
  value: string;
  options: string[];
  onChange: (value: string) => void;
  hasError?: boolean;
  ariaLabel: string;
}

// Custom scrollable select component (same as in DateTimePicker)
const ScrollableSelect: React.FC<ScrollableSelectProps> = ({
  value,
  options,
  onChange,
  hasError,
  ariaLabel,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const selectedRef = useRef<HTMLButtonElement>(null);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Scroll to selected value when opening
  useEffect(() => {
    if (isOpen && listRef.current && selectedRef.current) {
      const list = listRef.current;
      const selected = selectedRef.current;
      const listRect = list.getBoundingClientRect();
      const selectedRect = selected.getBoundingClientRect();

      // Center the selected item in the list
      const scrollTop = selected.offsetTop - (listRect.height / 2) + (selectedRect.height / 2);
      list.scrollTop = Math.max(0, scrollTop);
    }
  }, [isOpen]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    const currentIndex = options.indexOf(value);

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (currentIndex < options.length - 1) {
          onChange(options[currentIndex + 1]);
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (currentIndex > 0) {
          onChange(options[currentIndex - 1]);
        }
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        setIsOpen(false);
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        break;
    }
  }, [isOpen, options, value, onChange]);

  const handleSelect = (option: string) => {
    onChange(option);
    setIsOpen(false);
  };

  return (
    <div
      className="scrollable-select"
      ref={containerRef}
      onKeyDown={handleKeyDown}
    >
      <button
        type="button"
        className={`scrollable-select__trigger${hasError ? ' scrollable-select__trigger--error' : ''}${isOpen ? ' scrollable-select__trigger--open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        {value}
      </button>

      {isOpen && (
        <div className="scrollable-select__dropdown" role="listbox" ref={listRef}>
          {options.map((option) => (
            <button
              key={option}
              type="button"
              ref={option === value ? selectedRef : null}
              className={`scrollable-select__option${option === value ? ' scrollable-select__option--selected' : ''}`}
              onClick={() => handleSelect(option)}
              role="option"
              aria-selected={option === value}
            >
              {option}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

interface TimePickerProps {
  id: string;
  label: string;
  value: string; // Format: "HH:MM" (e.g., "14:30")
  onChange: (value: string) => void;
  required?: boolean;
  error?: string;
}

const TimePicker: React.FC<TimePickerProps> = ({
  id,
  label,
  value,
  onChange,
  required = false,
  error,
}) => {
  const [hour, setHour] = useState('12');
  const [minute, setMinute] = useState('00');

  useEffect(() => {
    if (value) {
      const [h, m] = value.split(':');
      setHour(h || '12');
      setMinute(m || '00');
    }
  }, [value]);

  const updateTime = (newHour: string, newMinute: string) => {
    onChange(`${newHour}:${newMinute}`);
  };

  const handleHourChange = (newHour: string) => {
    setHour(newHour);
    updateTime(newHour, minute);
  };

  const handleMinuteChange = (newMinute: string) => {
    setMinute(newMinute);
    updateTime(hour, newMinute);
  };

  // Generate hour options (00-23)
  const hours = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'));

  // Generate minute options (00, 05, 10, ..., 55)
  const minutes = Array.from({ length: 12 }, (_, i) => (i * 5).toString().padStart(2, '0'));

  return (
    <div className={`datetime-picker${error ? ' datetime-picker--error' : ''}`}>
      <label className="datetime-picker__label" htmlFor={id}>
        {label}
        {required && <span style={{ color: 'var(--danger, #dc3545)', marginLeft: '0.25rem' }} aria-hidden="true">*</span>}
      </label>
      <div className={`datetime-picker__time datetime-picker__time--standalone${error ? ' datetime-picker__time--error' : ''}`}>
        <ScrollableSelect
          value={hour}
          options={hours}
          onChange={handleHourChange}
          hasError={!!error}
          ariaLabel={`${label} hour`}
        />
        <span className="datetime-picker__separator">:</span>
        <ScrollableSelect
          value={minute}
          options={minutes}
          onChange={handleMinuteChange}
          hasError={!!error}
          ariaLabel={`${label} minute`}
        />
      </div>
      {error && <div className="form-error">{error}</div>}
    </div>
  );
};

export default TimePicker;
